/**
 * « Inéa sur WhatsApp » — Lot 3.1 (2026-09-03). Le membre lie son WhatsApp par
 * un code (10 min) envoyé au numéro Inéa (lien wa.me pré-rempli), puis parle à
 * Inéa depuis WhatsApp. Même patron que la carte Telegram, sur la page Profil.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Check, ExternalLink, Loader2, MessageCircle, RefreshCw, Unlink } from 'lucide-react';
import { ApiError, apiRequest } from '@/lib/api';
import { useFeatures } from '@/hooks/useFeatures';

interface WaStatus {
  enabled: boolean;
  status: 'none' | 'pending' | 'active' | 'revoked';
  number: string | null;
  link_code?: string | null;
  wa_me?: string | null;
  linked_at?: string | null;
  wa_id_masked?: string | null;
  profile_name?: string | null;
  session_open?: boolean;
}

const KEY = ['integrations', 'whatsapp'] as const;

export function WhatsAppIneaCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { whatsapp_member_enabled: enabled } = useFeatures();
  const status = useQuery({ queryKey: KEY, queryFn: () => apiRequest<WaStatus>('/integrations/whatsapp'), enabled, refetchInterval: (q) => (q.state.data?.status === 'pending' ? 5_000 : false) });
  const generate = useMutation({
    mutationFn: () => apiRequest<{ link_code: string; wa_me: string; expires_at: string; number: string }>('/integrations/whatsapp', { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const unlink = useMutation({
    mutationFn: () => apiRequest<void>('/integrations/whatsapp', { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  if (!enabled) return null;
  const d = status.data;
  const err = generate.error ?? unlink.error;

  return (
    <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.4 }} className="card mt-8 p-6">
      <header className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ background: '#25D366' }}>
          <MessageCircle size={18} />
        </span>
        <div className="min-w-0">
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">{t('waInea.title')}</p>
          <h2 className="font-display text-xl font-800 tracking-[-0.015em]">{t('waInea.subtitle')}</h2>
        </div>
      </header>

      {status.isLoading ? (
        <Loader2 size={18} className="animate-spin text-ink/40" />
      ) : d?.status === 'active' ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[13px] font-700 text-emerald-700"><Check size={14} /> {t('waInea.linked')} · {d.profile_name ?? d.wa_id_masked ?? ''}</p>
            <p className="mt-1 text-[12px] text-ink/55">{t('waInea.sessionHint')}</p>
            <p className="mt-1 text-[12px] text-ink/55">{t('waInea.number')} : {d.number}</p>
          </div>
          <div className="flex gap-2">
            <a href={`https://wa.me/${(d.number ?? '').replace(/^\+/, '')}`} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-1.5 px-3.5 py-2 text-[12.5px]"><ExternalLink size={13} /> {t('waInea.openWhatsApp')}</a>
            <button type="button" disabled={unlink.isPending} onClick={() => { if (window.confirm(t('waInea.confirmUnlink'))) unlink.mutate(); }} className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px]"><Unlink size={13} /> {t('waInea.unlink')}</button>
          </div>
        </div>
      ) : d?.status === 'pending' && d.link_code && d.wa_me ? (
        <div className="space-y-3">
          <ol className="space-y-1 text-[13px] text-ink/75">
            <li>{t('waInea.step1')}</li>
            <li>{t('waInea.step2')}</li>
            <li>{t('waInea.step3')}</li>
          </ol>
          <div className="flex flex-wrap items-center gap-3">
            <a href={d.wa_me} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px]" style={{ background: '#25D366' }}><MessageCircle size={14} /> {t('waInea.openWhatsApp')}</a>
            <div>
              <p className="text-[10.5px] font-700 uppercase tracking-[0.14em] text-ink/45">{t('waInea.codeLabel')}</p>
              <p className="font-mono text-[20px] font-800 tracking-[0.18em] text-ink">{d.link_code}</p>
            </div>
            <button type="button" disabled={generate.isPending} onClick={() => generate.mutate()} className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px]"><RefreshCw size={13} /> {t('waInea.relink')}</button>
          </div>
          <p className="text-[12px] text-ink/50">{t('waInea.pending')} · {t('waInea.number')} : {d.number}</p>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[13px] text-ink/60">{t('waInea.none')}</p>
          <button type="button" disabled={generate.isPending} onClick={() => generate.mutate()} className="btn-primary inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px]">
            {generate.isPending ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />} {t('waInea.link')}
          </button>
        </div>
      )}
      <p className="mt-4 text-[11.5px] text-ink/50">{t('waInea.aiNote')}</p>
      {err && <p role="alert" className="mt-2 rounded-xl bg-pink/10 px-3 py-2 text-[12.5px] font-600 text-pink-deep">{err instanceof ApiError ? err.message : t('publish.error')}</p>}
    </motion.section>
  );
}
